import { InputField } from '@/components/Form';
import { displayError, displaySuccess } from '@/utils/displayMessageUtils';
import { Button, Col, Row, Typography } from 'antd';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { householdExpenditureSchema } from '../../schema/householdExpenditureSchema';
import ApplicationHeading from '@/components/Elements/Heading/ApplicationHeading';
import ApplicationSubHeading from '@/components/Elements/Heading/ApplicationSubHeading';
import { theme } from 'antd';
import { formatNumber } from '@/utils/commonUtils';
import {
  useGetHouseholdExpenditureQuery,
  usePatchHouseholdExpenditureMutation,
} from '../../api/profileAPI';

const { useToken } = theme;

const HouseholdExpenditure = ({
  onValuesChange = () => {},
}: {
  onValuesChange?: (values: any) => void;
}) => {
  const { token } = useToken();
  const [
    patchHouseholdExpenditure,
    {
      isLoading: patchHouseholdExpenditureLoading,
      error: patchHouseholdExpenditureError,
    },
  ] = usePatchHouseholdExpenditureMutation();

  const { data: householdExpenditure, refetch: refetchHouseholdExpenditure } =
    useGetHouseholdExpenditureQuery();

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
    watch,
  } = useForm({
    resolver: yupResolver(householdExpenditureSchema),
  });

  useEffect(() => {
    const hemDetails = householdExpenditure?.data?.hem_details;
    if (hemDetails) {
      setValue(
        'childSpousalMaintenance',
        hemDetails.child_and_spousal_maintenance || 0,
      );
      setValue('childcare', hemDetails.childcare || 0);
      setValue(
        'clothingPersonalCare',
        hemDetails.clothing_and_personal_care || 0,
      );
      setValue('basicInsurances', hemDetails.general_basic_insurances || 0);
      setValue('groceries', hemDetails.groceries || 0);
      setValue('healthInsurance', hemDetails.health_insurance || 0);
      setValue('educationFees', hemDetails.higher_education_and_fees || 0);
      setValue('medicalHealth', hemDetails.medical_and_health || 0);
      setValue('petCare', hemDetails.pet_care || 0);
      setValue(
        'residenceCosts',
        hemDetails.primary_residence_running_costs || 0,
      );
      setValue(
        'privateSchooling',
        hemDetails.private_schooling_and_tuition || 0,
      );
      setValue('publicEducation', hemDetails.public_education || 0);
      setValue(
        'recreationEntertainment',
        hemDetails.recreation_and_entertainment || 0,
      );
      setValue(
        'insuranceLifeAccident',
        hemDetails.insurance_sickness_life || 0,
      );
      setValue(
        'mediaSubscriptions',
        hemDetails.communications_and_subscriptions || 0,
      );
      setValue('transport', hemDetails.transport || 0);
    }
  }, [householdExpenditure]);

  const onSubmit = async (data: any) => {
    try {
      const hemDetails = {
        child_and_spousal_maintenance:
          parseInt(data.childSpousalMaintenance) || 0,
        childcare: parseInt(data.childcare) || 0,
        clothing_and_personal_care: parseInt(data.clothingPersonalCare) || 0,
        general_basic_insurances: parseInt(data.basicInsurances) || 0,
        groceries: parseInt(data.groceries) || 0,
        health_insurance: parseInt(data.healthInsurance) || 0,
        higher_education_and_fees: parseInt(data.educationFees) || 0,
        medical_and_health: parseInt(data.medicalHealth) || 0,
        pet_care: parseInt(data.petCare) || 0,
        primary_residence_running_costs: parseInt(data.residenceCosts) || 0,
        private_schooling_and_tuition: parseInt(data.privateSchooling) || 0,
        public_education: parseInt(data.publicEducation) || 0,
        recreation_and_entertainment:
          parseInt(data.recreationEntertainment) || 0,
        insurance_sickness_life: parseInt(data.insuranceLifeAccident) || 0,
        communications_and_subscriptions:
          parseInt(data.mediaSubscriptions) || 0,
        transport: parseInt(data.transport) || 0,
      };

      const response = await patchHouseholdExpenditure({
        hem_details: hemDetails,
      });
      refetchHouseholdExpenditure();
      displaySuccess('Successfully Updated Household Expenditure.');
    } catch (err) {
      console.error('Error updating HEM data:', err);
      displayError('Something went wrong. Please try again later.');
    }
  };

  /**
   * TODO: API does not return foodGroceries till now so for now we can set default values
   */
  // useEffect(() => {
  //   if (profileDetail) {
  //     setValue('foodGroceries', profileDetail.foodGroceries ?? '');
  //   }
  // }, [profileDetail]);

  const [total, setTotal] = useState(0);
  const [basicTotal, setBasicTotal] = useState(0);
  const [nonBasicTotal, setNonBasicTotal] = useState(0);

  useEffect(() => {
    const basicFields = [
      'childcare',
      'clothingPersonalCare',
      'basicInsurances',
      'groceries',
      'healthInsurance',
      'medicalHealth',
      'petCare',
      'residenceCosts',
      'publicEducation',
      'recreationEntertainment',
      'mediaSubscriptions',
      'transport',
    ];

    const nonBasicFields = [
      'educationFees',
      'privateSchooling',
      'insuranceLifeAccident',
      'childSpousalMaintenance',
    ];

    const subscription = watch((values) => {
      const basicTotalValue = basicFields.reduce((acc, field) => {
        const value = (values as Record<string, any>)[field];
        const num = typeof value === 'string' ? parseInt(value, 10) : value;
        return acc + (isNaN(num) ? 0 : num);
      }, 0);

      const nonBasicTotalValue = nonBasicFields.reduce((acc, field) => {
        const value = (values as Record<string, any>)[field];
        const num = typeof value === 'string' ? parseInt(value, 10) : value;
        return acc + (isNaN(num) ? 0 : num);
      }, 0);

      setBasicTotal(basicTotalValue);
      setNonBasicTotal(nonBasicTotalValue);
      setTotal(basicTotalValue + nonBasicTotalValue);
    });

    return () => subscription.unsubscribe(); // cleanup
  }, [watch, setValue]);

  return (
    <div
      style={{
        width: '100%',
      }}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <>
          <Row
            justify="space-between"
            align="middle"
            gutter={[16, 16]}
            style={{
              padding: '16px',
              backgroundColor: '#fafafa',
              borderRadius: 8,
              border: '1px solid #f0f0f0',
              marginBottom: 32,
            }}
          >
            <Col xs={24} md={12}>
              <ApplicationHeading
                style={{ margin: 0 }}
                title="Household Expenditure (Monthly)"
              />
            </Col>
            <Col xs={24} md={12}>
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  justifyContent: 'flex-end',
                  gap: 16,
                  alignItems: 'center',
                }}
              >
                <div
                  style={{
                    padding: '0px 20px',
                    backgroundColor: '#f5f5f5',
                    borderRadius: 8,
                    border: '1px solid #d9d9d9',
                    fontSize: 15,
                    fontWeight: 400,
                    lineHeight: '40px',
                  }}
                >
                  <span style={{ color: '#888' }}>Grand Total | </span>
                  <Typography.Text
                    style={{ color: token.colorPrimary, fontWeight: 600 }}
                  >
                    {formatNumber(total)}
                  </Typography.Text>
                  <span style={{ marginLeft: 8, color: '#888' }}>
                    AUD per month
                  </span>
                </div>
                <Button
                  loading={patchHouseholdExpenditureLoading}
                  disabled={patchHouseholdExpenditureLoading}
                  type="primary"
                  htmlType="submit"
                  size="large"
                  style={{ minWidth: 120, borderRadius: 8 }}
                >
                  Save
                </Button>
              </div>
            </Col>
          </Row>
          <Row
            justify="space-between"
            align="middle"
            gutter={[16, 16]}
            style={{
              padding: '5px',
              backgroundColor: '#fafafa',
              borderRadius: 8,
              border: '1px solid #f0f0f0',
              marginBottom: 32,
            }}
          >
            <Col xs={24} lg={12}>
              <ApplicationSubHeading
                style={{ margin: '0.5rem 0' }}
                title="Basic Expenditure"
              />
            </Col>
            <Col xs={24} lg={12}>
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  justifyContent: 'flex-end',
                  gap: 16,
                  alignItems: 'center',
                }}
              >
                <div
                  style={{
                    padding: '0px 20px',
                    backgroundColor: '#f5f5f5',
                    borderRadius: 8,
                    border: '1px solid #d9d9d9',
                    fontSize: 15,
                    fontWeight: 400,
                    lineHeight: '36px',
                  }}
                >
                  <span style={{ color: '#888' }}>AUD | </span>
                  <Typography.Text
                    style={{ color: token.colorPrimary, fontWeight: 600 }}
                  >
                    {formatNumber(basicTotal)}
                  </Typography.Text>
                  <span style={{ marginLeft: 8, color: '#888' }}>
                    per month
                  </span>
                </div>
              </div>
            </Col>
          </Row>
          <Row gutter={[24, 32]}>
            <Col xs={24} md={24} lg={12}>
              <InputField
                type="number"
                name="childcare"
                control={control}
                label="Childcare"
                size="large"
                placeholder="Enter Amount"
                suffix="AUD per month"
                required={true}
                error={errors?.childcare?.message ?? ''}
              />
            </Col>

            <Col xs={24} md={24} lg={12}>
              <InputField
                type="number"
                name="clothingPersonalCare"
                control={control}
                label="Clothing and Personal Care"
                size="large"
                placeholder="Enter Amount"
                suffix="AUD per month"
                required={true}
                error={errors?.clothingPersonalCare?.message ?? ''}
              />
            </Col>

            <Col xs={24} md={24} lg={12}>
              <InputField
                type="number"
                name="basicInsurances"
                control={control}
                label="General Basic Insurances"
                size="large"
                placeholder="Enter Amount"
                suffix="AUD per month"
                required={true}
                error={errors?.basicInsurances?.message ?? ''}
              />
            </Col>

            <Col xs={24} md={24} lg={12}>
              <InputField
                type="number"
                name="groceries"
                control={control}
                label="Groceries"
                size="large"
                placeholder="Enter Amount"
                suffix="AUD per month"
                required={true}
                error={errors?.groceries?.message ?? ''}
              />
            </Col>

            <Col xs={24} md={24} lg={12}>
              <InputField
                type="number"
                name="healthInsurance"
                control={control}
                label="Health Insurance"
                size="large"
                placeholder="Enter Amount"
                suffix="AUD per month"
                required={true}
                error={errors?.healthInsurance?.message ?? ''}
              />
            </Col>

            <Col xs={24} md={24} lg={12}>
              <InputField
                type="number"
                name="medicalHealth"
                control={control}
                label="Medical and Health"
                size="large"
                placeholder="Enter Amount"
                suffix="AUD per month"
                required={true}
                error={errors?.medicalHealth?.message ?? ''}
              />
            </Col>

            <Col xs={24} md={24} lg={12}>
              <InputField
                type="number"
                name="petCare"
                control={control}
                label="Pet Care"
                size="large"
                placeholder="Enter Amount"
                suffix="AUD per month"
                required={true}
                error={errors?.petCare?.message ?? ''}
              />
            </Col>

            <Col xs={24} md={24} lg={12}>
              <InputField
                type="number"
                name="residenceCosts"
                control={control}
                label="Primary Residence Running Costs"
                size="large"
                placeholder="Enter Amount"
                suffix="AUD per month"
                required={true}
                error={errors?.residenceCosts?.message ?? ''}
              />
            </Col>

            <Col xs={24} md={24} lg={12}>
              <InputField
                type="number"
                name="publicEducation"
                control={control}
                label="Public or Government Primary and Secondary Education"
                size="large"
                placeholder="Enter Amount"
                suffix="AUD per month"
                required={true}
                error={errors?.publicEducation?.message ?? ''}
              />
            </Col>

            <Col xs={24} md={24} lg={12}>
              <InputField
                type="number"
                name="recreationEntertainment"
                control={control}
                label="Recreation and Entertainment"
                size="large"
                placeholder="Enter Amount"
                suffix="AUD per month"
                required={true}
                error={errors?.recreationEntertainment?.message ?? ''}
              />
            </Col>

            <Col xs={24} md={24} lg={12}>
              <InputField
                type="number"
                name="mediaSubscriptions"
                control={control}
                label="Telephone, Internet, PayTV and Media Streaming Subscriptions"
                size="large"
                placeholder="Enter Amount"
                suffix="AUD per month"
                required={true}
                error={errors?.mediaSubscriptions?.message ?? ''}
              />
            </Col>

            <Col xs={24} md={24} lg={12}>
              <InputField
                type="number"
                name="transport"
                control={control}
                label="Transport"
                size="large"
                placeholder="Enter Amount"
                suffix="AUD per month"
                required={true}
                error={errors?.transport?.message ?? ''}
              />
            </Col>
          </Row>
          <Row
            justify="space-between"
            align="middle"
            gutter={[16, 16]}
            style={{
              padding: '5px',
              backgroundColor: '#fafafa',
              borderRadius: 8,
              border: '1px solid #f0f0f0',
              marginBottom: 32,
            }}
          >
            <Col xs={24} md={12}>
              <ApplicationSubHeading
                style={{ margin: '0.5rem 0' }}
                title="Non-basic Expenditure"
              />
            </Col>
            <Col xs={24} md={12}>
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  justifyContent: 'flex-end',
                  gap: 16,
                  alignItems: 'center',
                }}
              >
                <div
                  style={{
                    padding: '0px 20px',
                    backgroundColor: '#f5f5f5',
                    borderRadius: 8,
                    border: '1px solid #d9d9d9',
                    fontSize: 15,
                    fontWeight: 400,
                    lineHeight: '36px',
                  }}
                >
                  <span style={{ color: '#888' }}>AUD | </span>
                  <Typography.Text
                    style={{ color: token.colorPrimary, fontWeight: 600 }}
                  >
                    {formatNumber(nonBasicTotal)}
                  </Typography.Text>
                  <span style={{ marginLeft: 8, color: '#888' }}>
                    per month
                  </span>
                </div>
              </div>
            </Col>
          </Row>
          <Row gutter={[24, 32]}>
            <Col xs={24} md={24} lg={12}>
              <InputField
                type="number"
                name="educationFees"
                control={control}
                label="Higher Education, Vocational Training and Professional Fees"
                size="large"
                placeholder="Enter Amount"
                suffix="AUD per month"
                required={true}
                error={errors?.educationFees?.message ?? ''}
              />
            </Col>
            <Col xs={24} md={24} lg={12}>
              <InputField
                type="number"
                name="privateSchooling"
                control={control}
                label="Private Schooling and Tuition"
                size="large"
                placeholder="Enter Amount"
                suffix="AUD per month"
                required={true}
                error={errors?.privateSchooling?.message ?? ''}
              />
            </Col>
            <Col xs={24} md={24} lg={12}>
              <InputField
                type="number"
                name="insuranceLifeAccident"
                control={control}
                label="Sickness and Personal Accident Insurance, Life Insurance"
                size="large"
                placeholder="Enter Amount"
                suffix="AUD per month"
                required={true}
                error={errors?.insuranceLifeAccident?.message ?? ''}
              />
            </Col>
            <Col xs={24} md={24} lg={12}>
              <InputField
                type="number"
                name="childSpousalMaintenance"
                control={control}
                label="Child and Spousal Maintenance"
                size="large"
                placeholder="Enter Amount"
                suffix="AUD per month"
                required={true}
                error={errors?.childSpousalMaintenance?.message ?? ''}
              />
            </Col>
          </Row>
        </>
      </form>
    </div>
  );
};

export default HouseholdExpenditure;
